import {
	Application,
	Extension,
	GeofenceLocation,
	OptionRelatedInstituteSettings,
} from "@verity/proctoring-option/types";
import { CSC_ORIGIN, ThirdPartyPlatform, VerityInstituteSettings } from "../../../types/common";
import { useCallback, useEffect, useState } from "react";
import { AssessmentUuids } from "../../../api/fetch";
import type { EnabledProctoringOptions } from "@verity/api-client/types";
import { intIdToUuid } from "../../../api/uuid";
import { useProctoringHelpers } from "../../proctoring-options/hooks/useProctoringHelpers";
import { UseEditQuizStateProps } from "./useEditQuizState";
import i18nPackage from "../../../i18n/i18n";
import type { CoreFileItem } from "../../../core";
import { coreToast } from "../../../core";
import {
	fetchReferenceMaterialsWithNativeFiles,
	REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES,
	saveFilesToFolder,
} from "../../ReferenceMaterialHelper";
import { SETUP_GUIDE_ATTRIBUTES } from "../../../setup-guide";
import { useEditQuizToast } from "../../context/EditQuizContext";
import type { AccountType } from "@verity/database/prisma";
import { InstituteSetupGuide } from "@yujacorp/verity-api-contract";
import { checkIfLmsEntryPoint } from "../../editQuizHelper";

type UseSettingsStateProps = Pick<
	UseEditQuizStateProps,
	| "quizInfo"
	| "logger"
	| "api"
	| "proctoringOption"
	| "courseName"
	| "assessmentUuids"
	| "quizType"
	| "proctoringAssessmentConfiguration"
	| "onSettingsLoadingChange"
	| "entryPoint"
>;

type AddAssessmentIdProps = Omit<AssessmentUuids, "assessmentUuid"> & {
	assessmentUuid?: string;
	newQuizId?: string;
};

/**
 * Contains state related to settings and fetching...?
 */
export default function useSettingsState({
	api,
	quizInfo,
	logger,
	proctoringOption,
	courseName,
	assessmentUuids,
	existingReferenceMaterialFiles,
	initialReferenceMaterialFiles,
	quizType,
	proctoringAssessmentConfiguration,
	onSettingsLoadingChange,
	entryPoint,
}: UseSettingsStateProps & {
	existingReferenceMaterialFiles?: CoreFileItem[];
	initialReferenceMaterialFiles?: CoreFileItem[];
}) {
	const {
		getAllApplications,
		getAllExtensions,
		getThirdPartyPlatforms,
		getInstitute,
		getUserInstituteId,
		getAssessmentByAssessmentInfo,
		getCourseById,
		getCourseByLmsCourseId,
		getInstituteSettings,
		getSetupGuideState,
		getCourseSettings,
		getVerityInstituteSettings,
		getEnabledProctoringOptions,
		getReferenceMaterials,
		getReferenceMaterialUploadUrl,
		moveReferenceMaterialsFromTemp,
		deleteAllReferenceMaterials,
		getActiveSetupGuideStep,
	} = api;

	const [optionRelatedInstituteSettings, setOptionRelatedInstituteSettings] =
		useState<OptionRelatedInstituteSettings>();
	const [enabledProctoringOptions, setEnabledProctoringOptions] = useState<EnabledProctoringOptions>({
		proctoringMethod: ["automated", "live", "pop-in", "pop-in-post-review"],
	});
	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>();
	const [courseSettings, setCourseSettings] = useState<Awaited<ReturnType<typeof getCourseSettings>>>();
	// Source of truth for setup guide workflow since anytime the service worker can tell us to switch modes
	const [isSetupGuideWorkflow, setIsSetupGuideWorkflow] = useState<boolean>(false);
	const [apiSetupGuideState, setApiSetupGuideState] = useState<InstituteSetupGuide>();
	const [accountType, setAccountType] = useState<AccountType>();

	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const [thirdPartyPlatforms, setThirdPartyPlatforms] = useState<ThirdPartyPlatform[]>([]);
	const [loading, setLoading] = useState(true);
	const [referenceMaterialFiles, setReferenceMaterialFiles] = useState<ReferenceMaterialType[]>();
	const [previousReferenceMaterialFiles, setPreviousReferenceMaterialFiles] = useState<ReferenceMaterialType[]>();
	const toastContainerId = useEditQuizToast();
	const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;

	useEffect(() => {
		onSettingsLoadingChange?.(loading);
	}, [loading, onSettingsLoadingChange]);

	// For fetches that don't require parameters
	const fetchStaticData = useCallback(async () => {
		const [appRes, extRes, thirdPartyRes, veritySettingsRes, enableRes, setupGuideRes, activeStepRes] =
			await Promise.all([
				getAllApplications(),
				getAllExtensions(),
				getThirdPartyPlatforms(),
				getVerityInstituteSettings(),
				getEnabledProctoringOptions({ assessmentInfo: quizInfo, courseName }),
				getSetupGuideState({ queryParams: {} }),
				getActiveSetupGuideStep(),
			]);

		setApplications(appRes);
		setExtensions(extRes);
		setVerityInstituteSettings(veritySettingsRes);
		setEnabledProctoringOptions(enableRes);
		setApiSetupGuideState(setupGuideRes);

		const isSetupGuide = activeStepRes === "Create Assessment" && checkIfLmsEntryPoint(entryPoint);
		setIsSetupGuideWorkflow(isSetupGuide);

		const platforms = Object.values(thirdPartyRes.selectedPlatformRoster);
		platforms.push({
			platformName: i18nPackage.t("editQuizPage.confirmationBlockTSX.defaultPlatform"),
			platformId: -1,
		});
		setThirdPartyPlatforms(platforms);

		return {
			applications: appRes,
			extensions: extRes,
			verityInstituteSettings: veritySettingsRes,
			enabledProctoringOption: enableRes,
			thirdPartyPlatforms: platforms,
			setupGuide: setupGuideRes,
			isSetupGuide,
		};
	}, [
		courseName,
		getAllApplications,
		getAllExtensions,
		getEnabledProctoringOptions,
		getSetupGuideState,
		getThirdPartyPlatforms,
		getVerityInstituteSettings,
		quizInfo,
		getActiveSetupGuideStep,
		entryPoint,
	]);

	// For fetches that require an institute uuid
	const fetchInstituteDataByUuid = useCallback(
		async (instituteUuid: string) => {
			const [instituteSettingRes] = await Promise.all([
				getInstituteSettings(instituteUuid).catch((e) => console.error(e)),
			]);
			if (instituteSettingRes) {
				setOptionRelatedInstituteSettings(instituteSettingRes);
			}
		},
		[getInstituteSettings],
	);

	const fetchCourseData = useCallback(
		async (courseId: number) => {
			const settings = await getCourseSettings(courseId);
			setCourseSettings(settings);
		},
		[getCourseSettings],
	);

	const getAssessmentUuids = useCallback(async () => {
		const quizId = quizInfo.quizId;
		let instituteId: number | undefined;
		try {
			instituteId = await getUserInstituteId();
		} catch (e) {
			void logger?.error(e);
		}
		let assessment: Awaited<ReturnType<typeof getAssessmentByAssessmentInfo>> | undefined = undefined;

		if (quizId) {
			assessment = await getAssessmentByAssessmentInfo({
				...quizInfo,
				quizId,
			});
		}
		let course: { accessControlUuid: string; instituteId: number; id: number };
		if (assessment) {
			course = await getCourseById(assessment.courseId);
		} else if (quizInfo.courseId) {
			course = await getCourseByLmsCourseId({
				lmsCourseId: quizInfo.courseId,
				instituteId,
			});
		} else {
			throw new Error("Course could not be found");
		}

		const institute = await getInstitute(course.instituteId);
		setAccountType(institute.accountType);

		const response = {
			assessmentUuid: assessment ? intIdToUuid(assessment.id) : undefined,
			courseUuid: course.accessControlUuid,
			instituteUuid: institute.uuid,
		};
		assessmentUuids.current = response;
		return {
			assessmentUUids: response,
			verityCourseId: course.id,
			instituteId: institute.id,
		};
	}, [
		getAssessmentByAssessmentInfo,
		getCourseById,
		getCourseByLmsCourseId,
		getInstitute,
		getUserInstituteId,
		logger,
		quizInfo,
		assessmentUuids,
	]);

	const addAssessmentId = useCallback(
		async ({
			instituteUuid,
			courseUuid,
			assessmentUuid,
			newQuizId,
		}: AddAssessmentIdProps): Promise<AssessmentUuids | null> => {
			let newAssessmentUuid: string;
			const quizId = newQuizId ?? quizInfo.quizId;
			if (!quizId) {
				return null;
			}
			if (assessmentUuid) {
				newAssessmentUuid = assessmentUuid;
			} else {
				const assessment = await getAssessmentByAssessmentInfo({
					...quizInfo,
					quizId: quizId,
				});
				if (!assessment) {
					throw new Error("Assessment not found");
				}
				newAssessmentUuid = intIdToUuid(assessment.id);
				assessmentUuids.current = { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
			}
			return { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
		},
		[assessmentUuids, getAssessmentByAssessmentInfo, quizInfo],
	);

	const { allowedCategories } = useProctoringHelpers({
		enabledProctoringOptions,
		isThirdPartyEnabled: Boolean(proctoringAssessmentConfiguration?.thirdPartyAssessmentOption),
		optionRelatedInstituteSettings,
		proctoringOption,
		quizType,
		verityInstituteSettings,
	});

	const isProctoringOptionAllowed = useCallback(
		<Option extends keyof EnabledProctoringOptions>(option: Option, value: EnabledProctoringOptions[Option][number]) =>
			enabledProctoringOptions[option].includes(value),
		[enabledProctoringOptions],
	);

	const fetchReferenceMaterialFiles = useCallback(async () => {
		if (existingReferenceMaterialFiles) {
			setReferenceMaterialFiles(existingReferenceMaterialFiles);
			setPreviousReferenceMaterialFiles(existingReferenceMaterialFiles);
			return;
		}

		// Second, not first: `existingReferenceMaterialFiles` is what the instructor confirmed on a save that
		// has already happened this session, and it has to win over what the wizard was first opened with.
		if (initialReferenceMaterialFiles) {
			setReferenceMaterialFiles(initialReferenceMaterialFiles);
			setPreviousReferenceMaterialFiles(initialReferenceMaterialFiles);
			return;
		}

		const quizId = quizInfo.quizId;
		if (!quizId) return;

		try {
			const assessment = await getAssessmentByAssessmentInfo({ ...quizInfo, quizId });
			if (!assessment) return;
			const filesWithNative = await fetchReferenceMaterialsWithNativeFiles(
				assessment.id.toString(),
				getReferenceMaterials,
			);
			setReferenceMaterialFiles(filesWithNative);
			setPreviousReferenceMaterialFiles(filesWithNative);
		} catch (e) {
			setReferenceMaterialFiles([]);
			setPreviousReferenceMaterialFiles([]);
			void logger?.error(e);
		}
	}, [
		existingReferenceMaterialFiles,
		initialReferenceMaterialFiles,
		getAssessmentByAssessmentInfo,
		getReferenceMaterials,
		logger,
		quizInfo,
	]);

	async function saveReferenceMaterials(quizId?: string, files?: CoreFileItem[]): Promise<void> {
		if (!quizId) {
			quizId = quizInfo.quizId;
		}
		if (!files) {
			files = referenceMaterialFiles;
		}

		if (!quizId || !files) return;

		try {
			const assessment = await getAssessmentByAssessmentInfo({ ...quizInfo, quizId });
			if (!assessment) return;
			await saveFilesToFolder(
				assessment.id.toString(),
				files,
				getReferenceMaterialUploadUrl,
				moveReferenceMaterialsFromTemp,
				deleteAllReferenceMaterials,
			);
			setPreviousReferenceMaterialFiles(files);
		} catch (e) {
			void logger?.error(e);
			throw new Error("UPLOAD_FAIL");
		}
	}

	/**Validates reference material settings, showing a toast if invalid (unless showToast is false). Returns true if valid. */
	const validateReferenceMaterialSetting = useCallback(
		({ showToast = true }: { showToast?: boolean } = {}): boolean => {
			if (proctoringOption?.referenceMaterialMode !== "instructor") return true;

			if (!referenceMaterialFiles || referenceMaterialFiles.length === 0) {
				if (showToast) {
					coreToast.error(
						i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.noFile"),
						toastOptions,
					);
				}
				return false;
			}

			const hasOversizedFile = referenceMaterialFiles.some(
				(file) => file.size > REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES,
			);
			if (hasOversizedFile) {
				if (showToast) {
					coreToast.error(
						i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.referenceMaterial.fileTooLargeDesc"),
						toastOptions,
					);
				}
				return false;
			}

			return true;
		},
		[proctoringOption?.referenceMaterialMode, referenceMaterialFiles, toastOptions],
	);

	useEffect(() => {
		if (!isSetupGuideWorkflow) return;
		const handleSetupGuideMessage = (event: MessageEvent) => {
			try {
				const expectedOrigin = new URL(CSC_ORIGIN).origin;

				if (event.origin !== expectedOrigin) {
					return;
				}
			} catch (e) {
				console.error("[EditQuiz] Invalid CSC_ORIGIN URL configured:", CSC_ORIGIN);
				return;
			}

			if (event.source !== window && event.source !== window.parent && event.source !== window.top) {
				return;
			}

			const request = event.data as {
				type: string;
			};

			if (request.type === SETUP_GUIDE_ATTRIBUTES.MESSAGE.TYPE.CLEANUP) {
				window.removeEventListener("message", handleSetupGuideMessage);
				setIsSetupGuideWorkflow(false);
			}
		};

		window.addEventListener("message", handleSetupGuideMessage);

		return () => {
			window.removeEventListener("message", handleSetupGuideMessage);
		};
	}, [isSetupGuideWorkflow]);

	return {
		action: {
			fetchStaticData,
			fetchInstituteDataByUuid,
			fetchCourseData,
			getAssessmentUuids,
			setLoading,
			addAssessmentId,
			isProctoringOptionAllowed,
			setReferenceMaterialFiles,
			fetchReferenceMaterialFiles,
			saveReferenceMaterials,
			validateReferenceMaterialSetting,
		},
		state: {
			proctoringData: {
				applications,
				extensions,
				thirdPartyPlatforms,
				instituteKeywords: optionRelatedInstituteSettings?.flaggedKeywords ?? [],
				geofences: (courseSettings?.geofenceLocations as GeofenceLocation[]) ?? [],
			},
			assessmentUuids,
			verityInstituteSettings,
			enabledProctoringOptions,
			optionRelatedInstituteSettings,
			loading,
			allowedCategories,
			referenceMaterialFiles,
			previousReferenceMaterialFiles,
			isSetupGuideWorkflow,
			apiSetupGuideState,
			accountType,
		},
	};
}

export type ProctoringDataType = ReturnType<typeof useSettingsState>["state"]["proctoringData"];
export type ReferenceMaterialType = CoreFileItem;
export {
	REFERENCE_MATERIAL_MAX_FILE_SIZE_BYTES,
	REFERENCE_MATERIAL_ACCEPTED_FILE_TYPES,
} from "../../ReferenceMaterialHelper";
